import React, {createContext, useContext, useEffect, useState,} from 'react';
import {useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useFormik} from 'formik';
import {useLocation} from 'react-router';
import moment from 'moment';
import * as _ from 'lodash';
import * as actions from '../_redux/actions/OrderDetailsActions';
import {getArrayFromString} from '../../_common/utils/getArrayFromString';
import {useOrderDetailsUIGlobalContext} from './OrderDetailsUIGlobalContext';
import {showGlobalMessage} from '../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {filterOptions} from '../../_common/utils/filterOptions';
import {getStatusList} from "../../_common/components/StatusList/actions/StatusListActions";
import {messages as M} from '../../../../_metronic/i18n/messages';
import {getContactsDetails} from '../utils/getContactsDetails';

const OrderDetailsUIOrderContext = createContext('');

export function useOrderDetailsUIOrderContext() {
  return useContext(OrderDetailsUIOrderContext);
}

export function OrderDetailsUIOrderProvider({children}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {orderID, users} = useOrderDetailsUIGlobalContext();
  const [inspectionsCount, setInspectionsCount] = useState(0);
  const [oldInspectionsForms, setOldInspectionForms] = useState(false);
  const [activeTab, setActiveTab] = useState('orderGeneral');
  const toggleOldInspectionForms = () => setOldInspectionForms(prevState => !prevState);

  const {orderDetails, orderDetailsLoading} = useSelector(
    (state) => ({
      orderDetails: state.orderDetailsReducer.orderDetails,
      orderDetailsLoading: state.orderDetailsReducer.orderDetailsLoading,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (orderID !== -1) {
      setOldInspectionForms(location.state?.oldInspectionsForms);
      getOrderData(orderID)
        .catch(console.error);
    }
  }, [orderID]);

  useEffect(() => {
    if (location.state?.oldInspectionsForms) {
      setOldInspectionForms(location.state?.oldInspectionsForms);
    } else if (orderDetails?.inspections) {
      const oldInspectionCount = orderDetails.inspections
        .filter(item => item?.originTypeID === 2)
        .length;
      setOldInspectionForms(oldInspectionCount > 0);
    }
  }, [orderDetails]);

  useEffect(() => {
    dispatch(getStatusList());
  }, []);

  const getOrderData = async (orderID) => await dispatch(actions.getOrderByID(orderID));

  const {
    customers,
    usingLawyer,
    lawyerName,
    shumaForOrderCount,
    dueDate,
    responsibleUser,
    orderStatus,
    paid,
    totalPayment,
    createTime
  } = orderDetails;

  const initialTabOrderGeneralValuesAddOrder = {
    customers: [],
    usingLawyer: false,
    lawyerName: '',
    contacts: [],
    shumaForOrderCount: '1',
    dueDate: null,
    responsibleUser: null,
    priceQuoteID: null,
    paid: '',
    totalPayment: ''
  };
  const initialTabOrderGeneralValuesEditOrder = {
    customers: getArrayFromString(customers) || [],
    usingLawyer: Boolean(usingLawyer) || false,
    lawyerName: lawyerName || '',
    contacts: getContactsDetails(orderDetails),
    shumaForOrderCount: shumaForOrderCount || '',
    dueDate: dueDate ? new Date(dueDate) : null,
    responsibleUser: (responsibleUser && users.length > 0)
      ? filterOptions(responsibleUser)(users)[0] : null,
    priceQuoteID: null,
    paid: paid || '',
    totalPayment: totalPayment || ''
  };
  const initialShumaForOrderCount = shumaForOrderCount;

  const validate = (values) => {
    let errors = {};
    const {customers, shumaForOrderCount} = values;

    if (customers.length === 0) {
      errors['customers'] = 'Must be at least one customer!';
    }
    if (!shumaForOrderCount) {
      errors['shumaForOrderCount'] = 'Must have value!';
    }
    if (orderID !== -1) {
      if (parseInt(initialShumaForOrderCount, 10) > parseInt(shumaForOrderCount, 10)) {
        errors['shumaForOrderCount'] = 'Can\'t be less than previous value';
      }
    }

    return errors;
  };

  const handleSubmit = async (data) => {
    const {
      usingLawyer,
      lawyerName,
      shumaForOrderCount,
      customers,
      dueDate,
      responsibleUser,
      priceQuoteID,
      contacts,
      ...rest
    } = data;

    const mappedCustomersValue = customers.map(item => parseInt(item.customerID, 10));

    const editedData = {
      orderID,
      customers: mappedCustomersValue,
      shumaForOrderCount,
      usingLawyer,
      lawyerName: usingLawyer ? lawyerName : '',
      dueDate: dueDate ? moment(dueDate).format('YYYY-MM-DD') : '',
      responsibleUser: responsibleUser ? responsibleUser.value : null,
      priceQuoteID: priceQuoteID || null,
      contactID: _.get(contacts[0], 'contactID', null),
      ...rest
    };
    setInspectionsCount(shumaForOrderCount);

    try {
      const orderID = await actions.createEditOrder(editedData);
      if (orderID && data.orderID === -1) {
        setActiveTab('orderInspections');
      }
      await getOrderData(orderID);
      await dispatch(showGlobalMessage('success', M['0']));
      history.push(`/order-details/${orderID}`, {oldInspectionsForms});
    } catch (e) {
      console.error(`Can\'t create order, ${e}`);
    }
  };

  const formikOrderGeneral = useFormik({
    initialValues: orderID !== -1
      ? {...initialTabOrderGeneralValuesEditOrder}
      : {...initialTabOrderGeneralValuesAddOrder},
    onSubmit: () => {
    },
    validate,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const value = {
    nextTabsEnabled: orderID !== -1,
    shumaForOrderCount,
    orderDetailsLoading,
    handleSubmit,
    formikOrderGeneral,
    inspectionsCount,
    getOrderData,
    oldInspectionsForms,
    toggleOldInspectionForms,
    orderStatus,
    createTime,
    activeTab,
    setActiveTab
  };

  return (
    <OrderDetailsUIOrderContext.Provider value={value}>
      {children}
    </OrderDetailsUIOrderContext.Provider>
  );
}

OrderDetailsUIOrderProvider.propTypes = {
  children: PropTypes.node,
};
